import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { tripService } from '../../../services/tripService';

const TripSummaryCard = ({ tripData, isVisible, onDismiss, user }) => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savedTripId, setSavedTripId] = useState(null);
  const [error, setError] = useState('');

  const handleProceedToConfirmation = () => {
    // Navigate to confirmation page with trip data
    navigate('/trip-confirmation', { state: { tripData } });
  };

  const handleSaveTrip = async () => {
    try {
      setIsSaving(true);

      if (!user) {
        setError('Please sign in to save trips');
        return;
      }

      // ✅ UPDATED: Extract timeframe handling - support both dates and text
      const extractTimeframe = () => {
        // Check if trip has specific dates
        const hasStartDate = tripData?.startDate && tripData?.startDate !== 'TBD';
        const hasEndDate = tripData?.endDate && tripData?.endDate !== 'TBD';

        if (hasStartDate && hasEndDate) {
          // Both dates available - use them
          return {
            startDate: tripData?.startDate,
            endDate: tripData?.endDate,
            timeframeText: null
          };
        } else if (hasStartDate || hasEndDate) {
          // Only one date available - use it and add text explanation
          return {
            startDate: hasStartDate ? tripData?.startDate : null,
            endDate: hasEndDate ? tripData?.endDate : null,
            timeframeText: tripData?.timeframe || tripData?.dates || 'Flexible dates'
          };
        } else {
          // No dates - use text only (like "TBD", "Summer 2025", etc.)
          return {
            startDate: null,
            endDate: null,
            timeframeText: tripData?.timeframe || tripData?.dates || 'TBD'
          };
        }
      };

      const timeframeData = extractTimeframe();

      // Prepare trip data to save
      const tripDataToSave = {
        destination: tripData?.location || tripData?.destination || 'Unknown',
        park: tripData?.park || tripData?.location || 'State Park',
        description: tripData?.description || tripData?.summary || '',
        ...timeframeData, // ✅ Use extracted timeframe (dates or text)
        duration: tripData?.duration || null,
        travelers: tripData?.travelers || 2,
        status: 'draft',
        imageUrl: tripData?.imageUrl || tripData?.image,
        imageAlt: tripData?.imageAlt || `${tripData?.location} trip`,
        totalCost: tripData?.estimatedCost || tripData?.budget || 0,
        activities: tripData?.activities || tripData?.highlights || [],
        accommodation: tripData?.accommodation || '',
        notes: tripData?.notes || '',
        isPublic: false
      };

      // Prepare itinerary items
      const itineraryItems = (tripData?.itinerary || [])?.map((day, index) => ({
        dayNumber: index + 1,
        title: day?.title || `Day ${index + 1}`,
        description: day?.description || day?.activities?.join(', ') || '',
        startTime: day?.startTime || '09:00:00',
        endTime: day?.endTime || '17:00:00',
        location: day?.location || tripData?.location,
        activityType: day?.activityType || 'Sightseeing',
        cost: day?.cost || 0,
        notes: day?.notes || ''
      }));

      const savedTrip = await tripService?.createTripWithItinerary(
        tripDataToSave,
        itineraryItems,
        tripData
      );

      setSavedTripId(savedTrip?.id);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving trip:', err);
      setError(err?.message || 'Failed to save trip');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`fixed right-0 top-0 h-screen w-96 bg-background border-l border-border shadow-organic-lg transform transition-transform duration-300 ease-in-out z-40 ${
      isVisible ? 'translate-x-0' : 'translate-x-full'
    }`}>
      <div className="h-full flex flex-col">
        {/* Side Panel Header */}
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-success/10 rounded-organic flex items-center justify-center">
                <Icon name="CheckCircle" size={16} className="text-success" />
              </div>
              <div>
                <h3 className="font-heading font-heading-semibold text-card-foreground text-sm">
                  Trip Plan Ready!
                </h3>
                <p className="text-xs text-muted-foreground">
                  Review your itinerary
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onDismiss}
              className="h-8 w-8"
            />
          </div>
        </div>

        {/* Side Panel Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {tripData ? (
            <>
              {/* Trip Summary - NOW SHOWING CONVERSATION-SPECIFIC DATA */}
              <div className="bg-muted/50 rounded-organic p-3">
                <h4 className="font-heading font-heading-medium text-card-foreground mb-2 text-sm">
                  {tripData?.title || "Trip Plan from Discussion"}
                </h4>
                <div className="grid grid-cols-1 gap-2 text-xs">
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={14} className="text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">
                      {tripData?.location || tripData?.featuredParks?.join(', ') || "Parks from conversation"}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={14} className="text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">
                      {tripData?.duration || "Duration from chat"}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Users" size={14} className="text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">
                      {tripData?.groupSize || "Group size discussed"}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="DollarSign" size={14} className="text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">
                      {tripData?.budget || tripData?.estimatedCost || "Budget from discussion"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Trip Details */}
              {tripData && (
                <div className="space-y-4">
                  <h5 className="font-heading font-heading-medium text-card-foreground text-sm">
                    Trip Details
                  </h5>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="MapPin" size={16} className="text-primary flex-shrink-0" />
                      <span className="text-card-foreground">
                        {tripData?.location || tripData?.destination || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Icon name="Calendar" size={16} className="mr-2" />
                      <span>
                        {tripData?.startDate && tripData?.endDate && 
                         tripData?.startDate !== 'TBD' && tripData?.endDate !== 'TBD' 
                           ? `${tripData?.startDate} to ${tripData?.endDate}` 
                           : tripData?.timeframeText || tripData?.timeframe || tripData?.dates || 'Flexible timeframe'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Users" size={16} className="text-primary flex-shrink-0" />
                      <span className="text-card-foreground">
                        {tripData?.travelers || 2} travelers
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="DollarSign" size={16} className="text-primary flex-shrink-0" />
                      <span className="text-card-foreground">
                        {tripData?.estimatedCost || tripData?.budget || 0} total cost
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Trip Highlights - NOW UNIQUE TO CONVERSATION */}
              {tripData?.highlights && tripData?.highlights?.length > 0 && (
                <div className="space-y-2">
                  <h5 className="font-heading font-heading-medium text-card-foreground text-sm">
                    Highlights Include:
                  </h5>
                  <ul className="space-y-1.5">
                    {tripData?.highlights?.map((highlight, index) => (
                      <li key={index} className="flex items-start space-x-2 text-xs">
                        <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground leading-relaxed">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* State Park Image Preview - FROM ITINERARY */}
              {tripData?.itinerary?.[0]?.parkImage && (
                <div className="space-y-2">
                  <h5 className="font-heading font-heading-medium text-card-foreground text-sm">
                    Park Preview:
                  </h5>
                  <div className="rounded-organic overflow-hidden">
                    <img 
                      src={tripData?.itinerary?.[0]?.parkImage} 
                      alt={tripData?.itinerary?.[0]?.parkImageAlt || "State park scenic view"}
                      className="w-full h-32 object-cover"
                    />
                  </div>
                </div>
              )}

              {/* Featured Parks - FROM CONVERSATION */}
              {tripData?.featuredParks && tripData?.featuredParks?.length > 0 && (
                <div className="space-y-2">
                  <h5 className="font-heading font-heading-medium text-card-foreground text-sm">
                    Parks to Visit:
                  </h5>
                  <div className="space-y-1">
                    {tripData?.featuredParks?.map((park, index) => (
                      <div key={index} className="flex items-center space-x-2 text-xs">
                        <Icon name="MapPin" size={14} className="text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">{park}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity Types - FROM CONVERSATION */}
              {tripData?.activityTypes && tripData?.activityTypes?.length > 0 && (
                <div className="space-y-2">
                  <h5 className="font-heading font-heading-medium text-card-foreground text-sm">
                    Planned Activities:
                  </h5>
                  <div className="space-y-1">
                    {tripData?.activityTypes?.slice(0, 5)?.map((activity, index) => (
                      <div key={index} className="flex items-center space-x-2 text-xs">
                        <Icon name="Activity" size={14} className="text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">{activity}</span>
                      </div>
                    ))}
                    {tripData?.activityTypes?.length > 5 && (
                      <p className="text-xs text-muted-foreground ml-6">
                        +{tripData?.activityTypes?.length - 5} more activities
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Estimated Cost */}
              {tripData?.estimatedCost && (
                <div className="bg-primary/5 border border-primary/20 rounded-organic p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-card-foreground">
                      Estimated Cost:
                    </span>
                    <span className="text-sm font-heading-semibold text-primary">
                      {tripData?.estimatedCost}
                    </span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <Icon name="MapPin" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No trip data available
              </p>
            </div>
          )}
        </div>

        {/* Side Panel Footer - Action Buttons */}
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="space-y-2">
            <Button
              variant="default"
              onClick={handleProceedToConfirmation}
              iconName="ArrowRight"
              iconPosition="right"
              className="w-full text-sm"
              disabled={!tripData}
            >
              Review & Confirm
            </Button>
            <Button
              variant="outline"
              onClick={onDismiss}
              className="w-full text-sm"
            >
              Continue Planning
            </Button>
          </div>
        </div>
      </div>
      {/* Toggle Button - Visible when panel is closed */}
      {!isVisible && tripData && (
        <button
          onClick={() => {/* This will be handled by parent component */}}
          className="absolute -left-12 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-l-organic p-3 shadow-organic-lg transition-colors duration-200"
          aria-label="Open trip summary"
        >
          <Icon name="ChevronLeft" size={20} />
        </button>
      )}
    </div>
  );
};

export default TripSummaryCard;